/**
 * @license Copyright (c) 2023-2025, CKSource Holding sp. z o.o. All rights reserved.
 * For licensing, see LICENSE.md.
 */

import { useContext, type ReactNode } from 'react';
import type { SnippetsSectionProps } from '../Snippets/types';
import { Code } from './Code';
import { Link } from '@mui/material';
import { SkeletonLoader } from '../SkeletonLoader/SkeletonLoader';
import { SnippetsSection } from '../Snippets/SnippetsSection';
import { Text } from './Text';
import { SECTION_NAME_RUN, SECTION_NAME_SETUP } from '../../../constants/snippetsSectionNames';
import { InstallationSettingsContext } from '../../../contexts/InstallationSettingsContext';

export function Run( { number, isLoading }: SnippetsSectionProps ): ReactNode {
	const { selectedTechnology, selectedInstallMethod } = useContext( InstallationSettingsContext );
	const isCdn = selectedInstallMethod === 'cdn';
	const isJs = selectedTechnology === 'js';

	return (
		<SkeletonLoader number={ number } sectionTitle={ SECTION_NAME_RUN } isLoading={ isLoading }>
			<SnippetsSection headerCharacter={ number } title={ SECTION_NAME_RUN }>
				{ !( isCdn && isJs ) && (
					<span data-testid={ 'non-cdn-or-non-js-text' }>
						<Text>
							Run your application and verify that the editor loads correctly.
						</Text>
						<Text>
							<b>Note:</b> You can use any modern bundler
							(such as <Link target='_blank' href='https://vitejs.dev/guide/'>Vite</Link>)
							to build the project.
							{ !isCdn && (
								<span data-testid={ 'non-cdn-only-text' }>
									&nbsp;If a bundler is not an option, we recommend using the CDN-based setup
									(refer to the <i>{ SECTION_NAME_SETUP }</i> step).
								</span>
							) }
						</Text>
					</span>
				) }

				{ isCdn && isJs && (
					<Text dataTestId={ 'cdn-js-text' }>
						Make sure to launch a local HTTP server (like <Code>nginx</Code>, <Code>caddy</Code> or <Code>serve</Code>) that
						will serve the files that you created. It's required, as we are using <Code>type="module"</Code> in our scripts.
					</Text>
				) }
			</SnippetsSection>
		</SkeletonLoader>
	);
}
